import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import cx from 'classnames'
import toast from 'react-hot-toast'
import { yupResolver } from '@hookform/resolvers/yup'

import styles from './Appearance.module.css'

import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import InputFileUpload from 'Design/components/InputFileUpload'
import ColorPicker from 'Design/components/ColorPicker'
import Button from 'Design/components/Button'
import Select from 'Design/components/Select'
import HelpText from 'Design/components/HelpText'
// import SwitchInput from 'Design/components/SwitchInput'
import FormErrorText from 'Design/components/FormErrorText'
import Link from 'Design/components/Link'
import SwitchInput from 'Design/components/SwitchInput'
import Textarea from 'Design/components/Textarea'
import parseErrorMessage from 'Utils/error-message-parser'

import WidgetPreviewMiniMap from './WidgetPreviewMiniMap'
import WidgetPreview from './WidgetPreview'

import { useUpdateBotAppearanceMutation } from 'Services/bot-profiles'
import { useBotSettingsContext } from '../SettingsContext'
import appearanceFormValidationSchema from './appearance-form-validation-schema'
import ReactToolTip from 'Src/b/components/ReactTooltip/ReactTooltip'
import {
  DEFAULT_TEXT_COLOR_THEME,
  TEXT_COLOR_OPTIONS,
  DEFAULT_BRAND_COLOR,
  DEFAULT_LABEL_BG_COLOR,
  DEFAULT_LABEL_TEXT_COLOR,
  DEFAULT_BADGE_POSITION,
  BADGE_POSITION_OPTIONS
} from 'Src/constants'

const useIconPreview = (watch) => {
  const [iconBlob, setImageBlob] = useState(null)

  const iconFile = watch('iconFile')
  const iconUrl = watch('iconUrl')

  const reader = useMemo(() => {
    const fileReader = new FileReader()
    fileReader.addEventListener(
      'load',
      function () {
        // convert image file to base64 string
        setImageBlob(fileReader.result)
      },
      false
    )
    return fileReader
  }, [])

  useEffect(() => {
    if (iconFile && iconFile[0]) {
      reader.readAsDataURL(iconFile[0])
    } else {
      setImageBlob(null)
    }
  }, [iconFile, reader])

  const iconSrc = iconBlob || iconUrl || ''

  return {
    iconPreview: iconSrc
  }
}

const Appearance = () => {
  const bot = useBotSettingsContext()

  const appearanceSettings = bot.settings.appearance || {}

  const formMethods = useForm({
    defaultValues: {
      displayName: bot.display_name,
      iconFile: null,
      iconUrl: appearanceSettings.icon || '',
      brandColor: appearanceSettings.brandColor || DEFAULT_BRAND_COLOR,
      textColor: appearanceSettings.textColor || DEFAULT_TEXT_COLOR_THEME,
      showBadgeLabel: appearanceSettings.showBadgeLabel || false,
      badgeLabel: appearanceSettings.badgeLabel || '',
      showBadgeLogo: appearanceSettings.showBadgeLogo || false,
      badgeLabelBgColor: appearanceSettings.badgeLabelBgColor || DEFAULT_LABEL_BG_COLOR,
      badgeLabelTextColor: appearanceSettings.badgeLabelTextColor || DEFAULT_LABEL_TEXT_COLOR,
      badgePosition: appearanceSettings.badgePosition || DEFAULT_BADGE_POSITION
      // position: 'bottomRight',
      // showOpenWidgetOnLoad: false,
      // hideOnMobile: false
    },
    resolver: yupResolver(appearanceFormValidationSchema)
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    reset,
    getValues,
    setValue,
    watch
  } = formMethods

  const [update, result] = useUpdateBotAppearanceMutation()
  const showBadgeLabel = watch('showBadgeLabel')
  const badgeLabelText = watch('badgeLabel')

  const onFormSubmit = (data) => {
    const payload = {
      id: bot.id,
      displayName: data.displayName,
      iconFile: data.iconFile?.[0],
      brandColor: data.brandColor,
      textColor: data.textColor,
      showBadgeLabel: data.showBadgeLabel,
      showBadgeLogo: data.showBadgeLogo,
      badgeLabel: data.badgeLabel,
      badgeLabelBgColor: data.badgeLabelBgColor,
      badgeLabelTextColor: data.badgeLabelTextColor,
      badgePosition: data.badgePosition
    }

    if (data.iconUrl === '' && data.iconFile === null) {
      payload.icon = ''
    }

    const promise = update(payload).unwrap()
    toast.promise(promise, {
      loading: 'Updating appearance settings...',
      success: 'Appearance settings updated successfully.',
      error: parseErrorMessage()
    })

    promise.then((result) => {
      reset({
        displayName: result.display_name,
        iconFile: null, // reset binary if exists
        iconUrl: result.settings.appearance.icon || '',
        brandColor: result.settings.appearance.brandColor,
        textColor: result.settings.appearance.textColor,
        showBadgeLabel: result.settings.appearance.showBadgeLabel,
        badgeLabel: result.settings.appearance.badgeLabel || '',
        showBadgeLogo: result.settings.appearance.showBadgeLogo,
        badgeLabelBgColor: result.settings.appearance.badgeLabelBgColor,
        badgeLabelTextColor: result.settings.appearance.badgeLabelTextColor
      })
    })

    return promise
  }

  const handleRemoveIcon = () => {
    setValue('iconFile', null)
    setValue('iconUrl', '', { shouldDirty: true })
    setValue('showBadgeLogo', false)
  }

  const { iconPreview } = useIconPreview(watch)

  const disableSubmitButton = isSubmitting || result.isLoading || !isDirty

  return (
    <FormProvider {...formMethods}>
      <div className={styles.Container}>
        <div className={styles.Form}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <div className={styles.InputRow}>
              <div className={styles.InputCol}>
                <FormControl isInvalid={errors.displayName}>
                  <Label>Display Name</Label>
                  <Input {...register('displayName')} />
                  {errors.displayName && <FormErrorText>{errors.displayName.message}</FormErrorText>}
                </FormControl>
              </div>
            </div>

            <div className={styles.InputRow}>
              <div className={styles.InputCol}>
                <FormControl>
                  <Label>Icon / Logo</Label>
                  <InputFileUpload
                    {...register('iconFile')}
                    placeholder='Upload icon/logo'
                    accept={['.png', '.jpg', '.jpeg']}
                    disabled={result.isLoading}
                    onRemove={handleRemoveIcon}
                    value={getValues('iconFile')}
                  />
                  <HelpText>A square image is recommended.</HelpText>
                </FormControl>
              </div>
              <div className={styles.InputCol}>
                {iconPreview && (
                  <div className={styles.IconPreviewContainer}>
                    <img src={iconPreview} className={styles.IconPreview} loading='lazy' />
                    {!result.isLoading && (
                      <Link className={styles.RemoveIcon} onClick={handleRemoveIcon}>
                        Remove
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.InputRow}>
              <div className={styles.InputCol}>
                <FormControl isInvalid={errors.brandColor}>
                  <Label>Brand Color (#hex)</Label>
                  <Controller
                    control={control}
                    name='brandColor'
                    render={({ field: { value, onChange } }) => {
                      return <ColorPicker placeholder='123456' value={value} onChange={onChange} />
                    }}
                  />
                  {errors.brandColor && <FormErrorText>{errors.brandColor.message}</FormErrorText>}
                </FormControl>
              </div>
              <div className={styles.InputCol}>
                <FormControl>
                  <Label>Text Color</Label>
                  <Select {...register('textColor')}>
                    {TEXT_COLOR_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className={cx(styles.InputRow, styles.PositionContainer)}>
              <div className={styles.InputCol}>
                <FormControl isInvalid={errors.badgePosition}>
                  <Label>Position</Label>
                  <Select {...register('badgePosition')}>
                    {BADGE_POSITION_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  {errors.badgePosition && <FormErrorText>{errors.badgePosition.message}</FormErrorText>}
                </FormControl>
              </div>
              <div className={styles.InputCol}>
                <WidgetPreviewMiniMap />
              </div>
            </div>

            <div className={styles.HeadingRow}>
              <span className={styles.Heading}>Minimized State</span>
              <hr className={styles.HorizontalLine} />
            </div>
            <div className={styles.InputRow}>
              <div className={styles.InputCol}>
                <FormControl>
                  <Controller
                    control={formMethods.control}
                    name='showBadgeLabel'
                    render={({ field: { value, onChange } }) => {
                      return (
                        <SwitchInput checked={value} onChange={onChange}>
                          Show Label
                        </SwitchInput>
                      )
                    }}
                  />
                </FormControl>
              </div>

              <div className={styles.ShowLogoColumn}>
                <FormControl>
                  <Controller
                    control={formMethods.control}
                    name='showBadgeLogo'
                    render={({ field: { value, onChange } }) => {
                      return (
                        <ReactToolTip
                          {...(!!iconPreview && { open: false })}
                          asChild
                          className={styles.ToggleTooltip}
                          content='No Icon/Logo available'
                        >
                          <div className={cx({ [styles.Disabled]: !iconPreview })}>
                            <SwitchInput disabled={!iconPreview} checked={value} onChange={onChange}>
                              Show logo / Icon
                            </SwitchInput>
                          </div>
                        </ReactToolTip>
                      )
                    }}
                  />
                </FormControl>
              </div>
            </div>

            {showBadgeLabel && (
              <Fragment>
                <FormControl>
                  <div className={styles.TextAreaContainer}>
                    <Textarea
                      {...register('badgeLabel')}
                      maxLength='240'
                      className={styles.TextArea}
                      placeholder={`Eg: 'Need help? Chat with us!`}
                    />
                    <div className={styles.CharacterCounter}>
                      <span id='current'>{badgeLabelText.length}/240</span>
                    </div>
                  </div>
                  {errors.badgeLabel && <FormErrorText>{errors.badgeLabel.message}</FormErrorText>}
                </FormControl>

                <div className={styles.InputRow}>
                  <div className={styles.InputCol}>
                    <FormControl isInvalid={errors.badgeLabelBgColor}>
                      <Label>Background Color (#hex)</Label>
                      <Controller
                        control={control}
                        name='badgeLabelBgColor'
                        render={({ field: { value, onChange } }) => {
                          return <ColorPicker placeholder='123456' value={value} onChange={onChange} />
                        }}
                      />
                      {errors.badgeLabelBgColor && <FormErrorText>{errors.badgeLabelBgColor.message}</FormErrorText>}
                    </FormControl>
                  </div>
                  <div className={styles.InputCol}>
                    <FormControl>
                      <Label>Text Color</Label>
                      <Select {...register('badgeLabelTextColor')}>
                        {TEXT_COLOR_OPTIONS.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </Fragment>
            )}

            {/*
            <div className={styles.InputRow}>
              <div className={styles.InputCol}>
                <FormControl>
                  <SwitchInput>Show Open widget on load</SwitchInput>
                </FormControl>
              </div>
              <div className={styles.InputCol}>
                <FormControl>
                  <SwitchInput>Hide in mobile devices</SwitchInput>
                </FormControl>
              </div>
            </div> */}

            <Button type='submit' variant='primary' className={styles.Submit} disabled={disableSubmitButton}>
              Save
            </Button>
          </form>
        </div>

        <div className={styles.Preview}>
          <WidgetPreview brandImage={iconPreview} />
        </div>
      </div>
    </FormProvider>
  )
}

export default Appearance
